import styled from '@emotion/styled';
import { useContext, useEffect } from 'react';
import { buyerUserContext } from 'application/contexts/useBuyerUser';
import { buyerSupportChatContext } from 'application/contexts/useBuyerSupportChat';
import { UserIcon } from 'ui/components/elements/UserIcon/UserIcon';
import { Message } from 'ui/components/buyer/patterns/ChatRoom/Message';
import { MessageInput } from 'ui/components/features/MessageInput/MessageInput';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { mqUser } from 'utils/responsive';
import { IChat } from 'domain/entities/Chat/Chat';

const Wrapper = styled.div`
    font-size: var(--font-medium);
    margin: 0 auto;
    background-color: #f8f9fd;
    padding: 18px 50px;

    ${mqUser('SP')} {
        padding: 18px 15px;
    }
`;

const ChatContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    background-color: #fff;
    border-radius: 10px;
    min-height: calc(100vh - 200px);

    ${mqUser('SP')} {
        padding: 15px;
        min-height: calc(100vh - 150px);
    }
`;

const SupporterNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    font-weight: var(--font-bold);
    font-size: var(--font-x-large);
    padding-bottom: 20px;
    border-bottom: 1px solid #e0e0e0;

    ${mqUser('SP')} {
        flex-direction: column;
        align-items: flex-start;
        font-size: 0.9rem;
        gap: 10px;
    }
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    flex: 1;
    padding: 20px 0;
    min-height: 400px;
`;

const MessageInputContainer = styled.div`
    padding: 10px 0;
    border-top: 1px solid #e0e0e0;
`;

const NoMessage = styled.div`
    font-size: var(--font-medium);
    color: #5f5f5f;
    text-align: center;
    margin-top: 20px;
`;

export const BuyerSupportChat: React.FC = () => {
    const { loading, chatMessages, message, setMessage, sendMessage, fetchData } = useContext(buyerSupportChatContext);
    const { user } = useContext(buyerUserContext);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (!user?.buyer?.supporter_user) {
        return (
            <Wrapper>
                <PageTitle title="サポートチャット" />
                <ChatContainer>
                    <NoMessage>サポーターが設定されていません。</NoMessage>
                </ChatContainer>
            </Wrapper>
        );
    }

    const supporterUser = user.buyer.supporter_user;

    const onClickSendMessage = () => {
        if (message.trim()) {
            sendMessage();
        }
    };

    const getSenderUser = (chat: IChat) => {
        if (chat.sender_buyer_user?.id) {
            return chat.sender_buyer_user;
        }
        if (chat.sender_support_user?.id) {
            return chat.sender_support_user;
        }
        return null;
    };

    return (
        <Wrapper>
            <PageTitle title="サポートチャット" />
            <ChatContainer>
                <SupporterNameContainer>
                    <UserIcon size={40} src={supporterUser.icon_image || ''} />
                    <span>
                        サポーター: {supporterUser.last_name} {supporterUser.first_name}
                    </span>
                </SupporterNameContainer>
                <MessageContainer>
                    {chatMessages.length === 0 && !loading ? (
                        <NoMessage>まだメッセージがありません。</NoMessage>
                    ) : (
                        chatMessages.map((chat) => {
                            const sender = getSenderUser(chat);
                            return (
                                <Message
                                    key={chat.id}
                                    icon_image={sender?.icon_image || ''}
                                    name={sender ? `${sender.last_name} ${sender.first_name}` : ''}
                                    isSelf={chat?.sender_buyer_user?.id === user.id}
                                    date={chat.send_at}
                                    message={chat.message}
                                />
                            );
                        })
                    )}
                </MessageContainer>
                <MessageInputContainer>
                    <MessageInput
                        archived={false}
                        message={message}
                        setMessage={setMessage}
                        sendMessage={onClickSendMessage}
                        requesting={loading}
                        companyType="buyer"
                    />
                </MessageInputContainer>
            </ChatContainer>
        </Wrapper>
    );
};
